import React, { Component } from 'react';
import styled from 'styled-components';
import {
  makeExternalUrl,
  attachLocale,
  makeDomId,
  toSlug,
  trackAnalytics,
  trackAnalyticsNew,
  resolveAccessibleLabel,
} from '../../../../lib/utils';
import CheckIcon from '../../../svgs/CheckIcon';
import CardHoverState from '../shared/CardHoverState';
import {
  DATA_TRACK_ROLE_CONTAINER,
  DATA_TRACK_TYPE_CTA,
  DL_PAGE_NAME_MODAL_OFFER,
  DL_SEND_OFFER_VIEW,
  DOM_OFFER_CARD,
  DOM_SECONDARY_LINK,
  MODAL_OPEN_OFFER,
} from '../../../../lib/constants';
import OfferModal from '../OfferModal/OfferModal';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import { addEventToDataLayer } from '../../../../lib/dataLayerUtils';
import { onCTAClick } from '../../../../lib/atm';
import MilesOffer from '../shared/MilesOffer';
import PromoBadge from '../PromoBadge/PromoBadge';
import DateDisplay from '../DateDisplay/DateDisplay';
import EndDatesGroup from '../shared/EndDatesGroup';

const OfferCardDiv = styled.article`
  position: relative;
  width: ${p => p.theme.unit(33)};
  background-color: ${p => p.theme.c.bg.primary};
  box-shadow: 0px 2px 10px 0px ${({ theme }) => theme.c.border['box-shadow']};
  border-radius: 8px;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  :hover {
    box-shadow: 0px 16px 40px ${({ theme }) => theme.c.border['box-shadow']};
  }
`;

const MobileOfferDetailsButton = styled.div`
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  width: 100%;
  text-align: unset;
`;

const ExtraMilesContainer = styled.header`
  position: absolute;
  z-index: 1;
  padding-top: 16px;
  padding-left: 16px;
`;

// *** Card Body ***
const CardBody = styled.section`
  height: ${p => p.theme.unit(23)};
  position: relative;
`;

const CardImgContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  text-decoration: none;
  overflow: hidden;
`;

const CardImg = styled.img`
  height: 100%;
  box-sizing: border-box;
  padding-top: ${p => p.theme.unit(2)};
`;

const CardContent = styled.div`
  padding: ${p => p.theme.unit(1)} ${p => p.theme.unit(2)} ${p => p.theme.unit(3)};
  height: ${({ theme }) => theme.unit(42.75)};

  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const PartnerContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: ${p => p.theme.unit(2)};
`;

const ShopLogo = styled.img`
  max-width: ${p => p.theme.unit(p.isMobile ? 30 : 27)};
  max-height: ${p => p.theme.unit(4)};
`;

const DisplayName = styled.p`
  ${({ theme }) => theme.typographyRebrand.heading.xs}
  color: ${p => p.theme.c.text.primary};
  flex-grow: 1;
  flex-basis: 0%;
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.unit(1)};

  height: ${p => p.theme.unit(20.25)};
  overflow-y: auto;
`;

const ShortDescription = styled.div`
  ${({ theme }) => theme.typographyRebrand.body.md}
  color: ${p => p.theme.c.text.primary};
  display: block;
  max-height: ${p => p.theme.unit(8)};
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  flex-shrink: 0;
`;

const Group = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.unit(0.5)};

  flex-wrap: wrap;
`;

const MilesOfferContainer = styled.div`
  height: ${({ theme }) => theme.unit(6.25)};
  margin-bottom: ${({ theme }) => theme.unit(2)};

  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const OfferTypeBadge = styled.span`
  ${({ theme }) => theme.typographyRebrand.body.sm}
  color: ${p => p.theme.c.text.primary};
  background-color: ${p => p.theme.c.bg['feature-4']};
  padding: ${p => p.theme.unit(0.25)} ${p => p.theme.unit(2)};

  display: inline-block;
  border-radius: ${({ theme }) => theme.radius.lg};
`;

const OfferCode = styled.span`
  ${({ theme }) => theme.typographyRebrand.body.sm}
  color: ${p => p.theme.c.tag.text.primary};
  background-color: ${p => p.theme.c.tag.bg.secondary};
  padding: ${p => p.theme.unit(0.25)} ${p => p.theme.unit(2)};
  display: inline-block;
`;

const CouponDisclaimer = styled.div`
  ${({ theme }) => theme.typographyRebrand.body.md}
  font-weight: var(--typography-font-weight-bold);
  margin: ${p => p.theme.unit(0.5)} 0;
  color: ${p => p.theme.c.text.primary};
`;

// *** Card Footer ***
const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.unit(1)};
`;

const ExclusionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Exclusions = styled.span`
  ${({ theme }) => theme.typographyRebrand.body.md}
  color: ${p => p.theme.c.text.primary};
  text-decoration: none;
  padding: 0;
  border: none;
  background: none;
`;

const CheckmarkIcon = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: inline-flex;
`;

const CardTagSpan = styled.span`
  display: none;
`;

const emptyCallback = () => {};

// TODO: make into a dumb component
class OfferCard extends Component {
  constructor(props) {
    super(props);

    this.state = { isHovering: false };
    this.favouriteButton;
  }

  componentDidMount() {
    // After mounting, set focus to favourite button if applicable
    this.props.pageStateModifiers &&
      this.props.pageStateModifiers.setElementFocus &&
      this.props.pageStateModifiers.setElementFocus(
        this.props.pageContext,
        this.props.id,
        this.favouriteButton
      );
  }

  render() {
    const {
      id,
      slug,
      affiliateUrl,
      recencyOfOffersDays,
      imageAltText,
      exclusionsApply,
      viewType = 'Carousel',
      shortDescription,
      couponCode,
      shortHeader,
      descriptiveText,
      isFavourite,
      offerTypes,
      removeItemHandler = emptyCallback,
      saveItemHandler = emptyCallback,
      pageStateModifiers = {},
      shop = {},
      shop: {
        multiplier,
        siteWideMultiplier,
        displayName
      },
      locale,
      tags,
      dataLayerId,
      dataContextContainer,
      termsAndConditions,
      dataIndex,
      prepositionKey,
      linkTextPrepositions,
      pageContext,
      endDate: offerEndDate,
      startDate: offerStartDate,
    } = this.props;
    const {
      syncUserDataOnPage = emptyCallback,
      setModalBody = emptyCallback,
      isUserLoggedIn = emptyCallback,
      makeAndOpenSignInModal = emptyCallback,
      setActiveFocus = emptyCallback,
    } = pageStateModifiers;

    const {
      isHovering,
    } = this.state;

    const imageUrl = makeExternalUrl(this.props.imageUrl);

    const config = this.props.config || {};

    const {
      singularMilesText = '',
      pluralMilesText = '',
      singularFlatRateMilesText = '',
      pluralFlatRateMilesText = '',
      exclusionsApplyText = '',
      primaryText = '',
      primaryButtonTagSet = {},
      secondaryText = '',
      secondaryButtonTagSet = {},
      milesBadgeText = '',
      codeText = '',
      endsText = '',
      couponDisclaimerText = '',
      modalButtonOneText = '',
      modalButtonOneTagSet = {},
      modalButtonTwoText = '',
      modalButtonTwoTagSet = {},
      modalButtonTwoTextSaved = '',
      modalButtonTwoTagSetSaved = {},
      modalButtonThreeText = '',
      modalButtonThreeTagSet = {},
      modalCouponCodeTitle = '',
      modalTermsAndConditionsTitle = '',
      favouriteAddLabel = '',
      favouriteRemoveLabel = '',
    } = config;

    let baseMiles;
    let amountPerBaseMiles;
    let mediumImageAltText = '';
    let currency;
    let mediumImageUrl = '';
    let shopSlug = '';

    if (shop) {
      baseMiles = shop.baseMiles;
      amountPerBaseMiles = shop.amountPerBaseMiles;
      mediumImageAltText = shop.mediumImageAltText;
      currency = shop.currency;
      mediumImageUrl = makeExternalUrl(shop.mediumImageUrl);
      shopSlug = shop.slug;
    }

    const secondaryLabel = resolveAccessibleLabel(
      descriptiveText,
      shop,
      linkTextPrepositions,
      'for',
      secondaryText,
      shortHeader !== null ? shortHeader : shortDescription,
      prepositionKey !== null ? prepositionKey : 'to'
    );

    const offerTrackingLocation = `${DOM_OFFER_CARD}:${slug}`;
    const offerDetailsTrack = trackAnalyticsNew({
      url: '',
      location: offerTrackingLocation,
      name: 'offer-details',
      tags: secondaryButtonTagSet,
    });
    const favouriteTrack = trackAnalyticsNew({
      url: '',
      location: offerTrackingLocation,
      name: isFavourite ? 'unsave-offer' : 'save-offer',
    });

    let resolvedOfferSlug = MODAL_OPEN_OFFER;
    resolvedOfferSlug = attachLocale(resolvedOfferSlug, locale);

    // *** HANDLER ***
    const removeOfferHandler = () => {
      if (isUserLoggedIn()) {
        removeItemHandler(id, () => {
          setActiveFocus(pageContext, id);
          syncUserDataOnPage();
        });
      } else {
        makeAndOpenSignInModal();
      }
    };

    const saveOfferHandler = () => {
      if (isUserLoggedIn()) {
        saveItemHandler(id, offerEndDate, () => {
          setActiveFocus(pageContext, id);
          syncUserDataOnPage();
        });
      } else {
        makeAndOpenSignInModal();
      }
    };

    const renderStarIcon = ({ isMobile } = {}) => (
      <CheckmarkIcon
        innerRef={element => (this.favouriteButton = element)}
        aria-label={isFavourite ? favouriteRemoveLabel : favouriteAddLabel}
        title={isFavourite ? favouriteRemoveLabel : favouriteAddLabel}
        data-track-id={favouriteTrack.dataTrackingId}
        data-track-click={favouriteTrack.dataTrack}
        data-track-type={DATA_TRACK_TYPE_CTA}
        onClick={(event) => {
          if (isMobile) {
            event.stopPropagation();
          }
          if (isFavourite) {
            removeOfferHandler(isMobile);
          } else {
            saveOfferHandler(isMobile);
          }
        }}
        onMouseEnter={() => {
          this.setState({ isHovering: true });
        }}
        onMouseLeave={() => {
          this.setState({ isHovering: false });
        }}
      >
        <CheckIcon hover={isHovering} active={isFavourite} />
      </CheckmarkIcon>
    );

    const makeAndOpenModal = (isMobile = false) => {
      const modal = (
        <OfferModal
          isMobile={isMobile}
          id={id}
          slug={slug}
          affiliateUrl={affiliateUrl}
          recencyOfOffersDays={recencyOfOffersDays}
          imageAltText={imageAltText}
          exclusionsApply={exclusionsApply}
          viewType={viewType}
          shortDescription={shortDescription}
          couponCode={couponCode}
          shortHeader={shortHeader}
          isFavourite={isFavourite}
          offerTypes={offerTypes}
          removeItemHandler={removeItemHandler}
          saveItemHandler={saveItemHandler}
          shop={shop}
          locale={locale}
          tags={tags}
          dataLayerId={dataLayerId}
          dataContextContainer={dataContextContainer}
          offerStartDate={offerStartDate}
          offerEndDate={offerEndDate}
          imageUrl={imageUrl}
          singularMilesText={singularMilesText}
          pluralMilesText={pluralMilesText}
          singularFlatRateMilesText={singularFlatRateMilesText}
          pluralFlatRateMilesText={pluralFlatRateMilesText}
          exclusionsApplyText={exclusionsApplyText}
          milesBadgeText={milesBadgeText}
          codeText={codeText}
          endsText={endsText}
          couponDisclaimerText={couponDisclaimerText}
          baseMiles={baseMiles}
          amountPerBaseMiles={amountPerBaseMiles}
          mediumImageAltText={mediumImageAltText}
          mediumImageUrl={mediumImageUrl}
          currency={currency}
          termsAndConditions={termsAndConditions}
          modalButtonOneText={modalButtonOneText}
          modalButtonOneTagSet={modalButtonOneTagSet}
          modalButtonTwoText={modalButtonTwoText}
          modalButtonTwoTagSet={modalButtonTwoTagSet}
          modalButtonTwoTextSaved={modalButtonTwoTextSaved}
          modalButtonTwoTagSetSaved={modalButtonTwoTagSetSaved}
          modalButtonThreeText={modalButtonThreeText}
          modalButtonThreeTagSet={modalButtonThreeTagSet}
          modalCouponCodeTitle={modalCouponCodeTitle}
          modalTermsAndConditionsTitle={modalTermsAndConditionsTitle}
          pageStateModifiers={pageStateModifiers}
          descriptiveText={descriptiveText}
          prepositionKey={prepositionKey}
          linkTextPrepositions={linkTextPrepositions}
          favouriteAddLabel={favouriteAddLabel}
          favouriteRemoveLabel={favouriteRemoveLabel}
        />
      );

      // Add event to dataLayer
      const eventProps = {
        pageName: DL_PAGE_NAME_MODAL_OFFER,
        offerDataLayerId: dataLayerId,
      };
      addEventToDataLayer(DL_SEND_OFFER_VIEW, eventProps);

      setModalBody(modal, isMobile);
    };

    const openMobileOfferDetails = (event) => {
      onCTAClick(event);
      event.stopPropagation();
      makeAndOpenModal(true);
    };

    const DesktopBody = () => {
      const domId = makeDomId(DOM_OFFER_CARD, slug, false);
      const secondaryLinkSlug = toSlug(secondaryText);
      const secondaryLinkId = `${DOM_SECONDARY_LINK}_${secondaryLinkSlug}`;
      const secondaryTrack = trackAnalytics(
        domId,
        secondaryLinkId,
        resolvedOfferSlug,
        secondaryButtonTagSet
      );

      return (
        <OfferCardDiv
          {...secondaryTrack}
          className="hover-container"
          viewType={viewType}
          id={domId}
          data-layer-source-id={dataLayerId}
          data-context-container={dataContextContainer}
          data-index={dataIndex}
          dataLayerId={dataLayerId}
          dataContextContainer={dataContextContainer}
          pageStateModifiers={pageStateModifiers}
          shopSlug={shopSlug}
          data-track-role={DATA_TRACK_ROLE_CONTAINER}
          useDefaultOnClickHandler={false}
        >
          <CardTagSpan id={`${slug}_tag`}>{slug}</CardTagSpan>
          {multiplier ? (
            <ExtraMilesContainer>
              <PromoBadge
                extraMilesText={shop.config.extraMilesText}
                pluralFlatRateMilesText={pluralFlatRateMilesText}
              />
            </ExtraMilesContainer>
          ) : null}
          <CardBody>
            <div className="show-hover">
              <CardHoverState
                parentId={domId}
                shop={shop}
                shopSlug={shopSlug}
                offer={{
                  descriptiveText,
                  linkTextPrepositions,
                  prepositionKey,
                  shortHeader,
                  shortDescription,
                }}
                offerSlug={slug}
                locale={locale}
                tags={tags}
                secondaryLink={resolvedOfferSlug}
                primaryText={primaryText}
                primaryButtonTagSet={primaryButtonTagSet}
                secondaryText={secondaryText}
                secondaryButtonTagSet={secondaryButtonTagSet}
                dataLayerId={dataLayerId}
                dataContextContainer={dataContextContainer}
                makeAndOpenModal={() => makeAndOpenModal()}
                pageStateModifiers={pageStateModifiers}
                secondaryBehaviour="button"
                defaultOnClickHandler={true}
                trackingLocation={offerTrackingLocation}
                primaryTrackingName="shop-now"
                secondaryTrackingName="offer-details"
              />
            </div>

            <CardImgContainer>
              <CardImg src={imageUrl || mediumImageUrl} alt="" />
            </CardImgContainer>
          </CardBody>
          <CardContent>
            <div>
              <PartnerContainer>
                <ShopLogo src={mediumImageUrl} alt={mediumImageAltText} />
                <DisplayName>{displayName}</DisplayName>
              </PartnerContainer>
              <MilesOfferContainer>
                <MilesOffer
                  isOffer
                  multiplier={multiplier}
                  siteWideMultiplier={siteWideMultiplier}
                  baseMiles={baseMiles}
                  amountPerBaseMiles={amountPerBaseMiles}
                  singularMilesText={singularMilesText}
                  pluralMilesText={pluralMilesText}
                  singularFlatRateMilesText={singularFlatRateMilesText}
                  pluralFlatRateMilesText={pluralFlatRateMilesText}
                  milesBadgeText={milesBadgeText}
                  locale={locale}
                />
              </MilesOfferContainer>
              <DescriptionContainer>
                <ShortDescription>{shortDescription}</ShortDescription>
                <Group>
                  {offerTypes &&
                    offerTypes.length >= 1 &&
                    offerTypes.map((s, index) => {
                      return s.visibleInOffer ? (
                        <OfferTypeBadge key={`${s.name}-${index}`}>
                          {s.name}
                        </OfferTypeBadge>
                      ) : null;
                  })}
                  {couponCode && (
                    <div>
                      <CouponDisclaimer>{couponDisclaimerText}</CouponDisclaimer>
                      <OfferCode>
                        {codeText} {couponCode}
                      </OfferCode>
                    </div>
                  )}
                </Group>
              </DescriptionContainer>
            </div>
            <CardFooter>
              {(multiplier || siteWideMultiplier || offerEndDate) && (
                <EndDatesGroup>
                  <DateDisplay
                    preText={endsText}
                    dates={[
                      multiplier && multiplier.endDate,
                      siteWideMultiplier && siteWideMultiplier.endDate,
                      offerEndDate,
                    ]}
                    locale={locale}
                  />
                  </EndDatesGroup>
                )}
              <ExclusionsContainer>
                <Exclusions>{exclusionsApplyText}</Exclusions>
                {renderStarIcon()}
              </ExclusionsContainer>
            </CardFooter>
          </CardContent>
        </OfferCardDiv>
      );
    };

    const MobileBody = () => {
      const domId = makeDomId(DOM_OFFER_CARD, slug, true);
      const secondaryLinkSlug = toSlug(secondaryText);
      const secondaryLinkId = `${DOM_SECONDARY_LINK}_${secondaryLinkSlug}`;
      const secondaryTrack = trackAnalytics(
        domId,
        secondaryLinkId,
        resolvedOfferSlug,
        secondaryButtonTagSet
      );

      return (
        <OfferCardDiv
          {...secondaryTrack}
          id={domId}
          viewType={viewType}
          data-layer-source-id={dataLayerId}
          data-context-container={dataContextContainer}
          data-index={dataIndex}
          data-track-role={DATA_TRACK_ROLE_CONTAINER}
          makeAndOpenModal={() => {
            makeAndOpenModal(true);
          }}
          useDefaultOnClickHandler={false}
        >
          <MobileOfferDetailsButton
            data-track-id={offerDetailsTrack.dataTrackingId}
            data-track-click={offerDetailsTrack.dataTrack}
            data-track-type={DATA_TRACK_TYPE_CTA}
            onClick={openMobileOfferDetails}
            onKeyDown={(event) => {
              if (
                event.target === event.currentTarget
                && (event.key === 'Enter' || event.key === ' ')
              ) {
                event.preventDefault();
                openMobileOfferDetails(event);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={secondaryLabel}
          >
            <CardTagSpan id={`${slug}_tag`}>{slug}</CardTagSpan>
            {multiplier ? (
              <ExtraMilesContainer>
                <PromoBadge
                  extraMilesText={shop.config.extraMilesText}
                  pluralFlatRateMilesText={pluralFlatRateMilesText}
                />
              </ExtraMilesContainer>
            ) : null}
            <CardBody>
              <CardImgContainer>
                <CardImg src={imageUrl || mediumImageUrl} alt="" />
              </CardImgContainer>
            </CardBody>
            <CardContent>
                <PartnerContainer>
                  <ShopLogo isMobile={true} src={mediumImageUrl} alt={mediumImageAltText} />
                  <DisplayName>{displayName}</DisplayName>
                </PartnerContainer>

                <MilesOfferContainer>
                  <MilesOffer
                    isOffer
                    multiplier={multiplier}
                    siteWideMultiplier={siteWideMultiplier}
                    baseMiles={baseMiles}
                    amountPerBaseMiles={amountPerBaseMiles}
                    singularMilesText={singularMilesText}
                    pluralMilesText={pluralMilesText}
                    singularFlatRateMilesText={singularFlatRateMilesText}
                    pluralFlatRateMilesText={pluralFlatRateMilesText}
                    milesBadgeText={milesBadgeText}
                    locale={locale}
                  />
                </MilesOfferContainer>

                <DescriptionContainer>
                  <ShortDescription>{shortDescription}</ShortDescription>
                  <Group>
                    {offerTypes &&
                      offerTypes.length >= 1 &&
                      offerTypes.map((s, index) => {
                        return s.visibleInOffer ? (
                          <OfferTypeBadge key={`${s.name}-${index}`}>
                            {s.name}
                          </OfferTypeBadge>
                        ) : null;
                    })}
                    {couponCode && (
                      <div>
                        <CouponDisclaimer>
                          {couponDisclaimerText}
                        </CouponDisclaimer>
                        <OfferCode>
                          {codeText} {couponCode}
                        </OfferCode>
                      </div>
                    )}
                  </Group>
                </DescriptionContainer>

              <CardFooter>
                {(multiplier || siteWideMultiplier || offerEndDate) && (
                  <EndDatesGroup>
                    <DateDisplay
                      preText={endsText}
                      dates={[
                        multiplier && multiplier.endDate,
                        siteWideMultiplier && siteWideMultiplier.endDate,
                        offerEndDate,
                      ]}
                      locale={locale}
                    />
                  </EndDatesGroup>
                )}
                <ExclusionsContainer>
                  <Exclusions>{exclusionsApplyText}</Exclusions>
                  {renderStarIcon({ isMobile: true })}
                </ExclusionsContainer>
              </CardFooter>
            </CardContent>
          </MobileOfferDetailsButton>
        </OfferCardDiv>
      );
    };

    const renderComp = (
      <MediaQueryHelper
        desktopChildren={DesktopBody()}
        mobileChildren={MobileBody()}
      />
    );
    return renderComp;
  }
}

export default OfferCard;
